import React, { useState } from 'react';
import { Server } from 'lucide-react';

interface ServiceAccount {
  username: string;
  email: string;
  role: string;
  createdAt: string;
}

interface ServiceAccountSectionProps {
  existingServiceAccount?: ServiceAccount;
  onCreateServiceAccount: (data: {
    createServiceAccount: boolean;
    serviceAccountPasswordOption: 'generated' | 'custom' | 'ssh-key';
    serviceAccountPassword: string;
  }) => void;
  onPasswordReset?: (username: string) => void;
  onDelete?: (username: string) => void;
  disabled?: boolean;
}

export default function ServiceAccountSection({
  existingServiceAccount,
  onCreateServiceAccount,
  onPasswordReset,
  onDelete,
  disabled = false,
}: ServiceAccountSectionProps) {
  const [createServiceAccount, setCreateServiceAccount] = useState(false);
  const [serviceAccountPasswordOption, setServiceAccountPasswordOption] = useState<
    'generated' | 'custom' | 'ssh-key'
  >('generated');
  const [serviceAccountPassword, setServiceAccountPassword] = useState('');

  const handleChange = () => {
    onCreateServiceAccount({
      createServiceAccount,
      serviceAccountPasswordOption,
      serviceAccountPassword,
    });
  };

  React.useEffect(() => {
    handleChange();
  }, [createServiceAccount, serviceAccountPasswordOption, serviceAccountPassword]);

  return (
    <div className='border-t border-white/10 pt-4 mb-6'>
      {existingServiceAccount ? (
        // Existing Service Account Display
        <div className='bg-blue-500/10 border border-blue-500/20 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-blue-400 mb-3 flex items-center gap-2'>
            <Server className='w-4 h-4' />
            Existing Service Account
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-4'>
            <div>
              <span className='text-slate-400'>Username:</span>
              <span className='text-slate-200 ml-2 font-mono'>
                {existingServiceAccount.username}
              </span>
            </div>
            <div>
              <span className='text-slate-400'>Email:</span>
              <span className='text-slate-200 ml-2'>{existingServiceAccount.email}</span>
            </div>
            <div>
              <span className='text-slate-400'>Role:</span>
              <span className='text-slate-200 ml-2'>{existingServiceAccount.role}</span>
            </div>
            <div>
              <span className='text-slate-400'>Created:</span>
              <span className='text-slate-200 ml-2'>
                {new Date(existingServiceAccount.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          {onPasswordReset && onDelete && (
            <div className='flex gap-2'>
              <button
                onClick={() => onPasswordReset(existingServiceAccount.username)}
                disabled={disabled}
                className='px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50 transition-colors'
              >
                Reset Password
              </button>
              <button
                onClick={() => onDelete(existingServiceAccount.username)}
                disabled={disabled}
                className='px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 transition-colors'
              >
                Delete Service Account
              </button>
            </div>
          )}
        </div>
      ) : (
        // Create Service Account Option
        <div>
          <div className='flex items-center gap-3 mb-4'>
            <input
              type='checkbox'
              id='createServiceAccount'
              checked={createServiceAccount}
              onChange={e => setCreateServiceAccount(e.target.checked)}
              disabled={disabled}
              className='w-4 h-4 text-blue-600 bg-slate-700 border-white/10 rounded focus:ring-blue-500 disabled:opacity-50'
            />
            <label htmlFor='createServiceAccount' className='text-sm font-medium text-slate-200'>
              Create Service Account
            </label>
          </div>

          {createServiceAccount && (
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>
                  Password Type
                </label>
                <select
                  value={serviceAccountPasswordOption}
                  onChange={e =>
                    setServiceAccountPasswordOption(
                      e.target.value as 'generated' | 'custom' | 'ssh-key'
                    )
                  }
                  disabled={disabled}
                  className='w-full px-3 py-2 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none disabled:opacity-50'
                  aria-label='Service Account Password Type'
                >
                  <option value='generated'>System Generated</option>
                  <option value='custom'>Custom Password</option>
                  <option value='ssh-key'>SSH Key</option>
                </select>
              </div>

              {(serviceAccountPasswordOption === 'custom' ||
                serviceAccountPasswordOption === 'ssh-key') && (
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>
                    {serviceAccountPasswordOption === 'custom' ? 'Password' : 'SSH Key'}
                  </label>
                  <input
                    type={serviceAccountPasswordOption === 'custom' ? 'password' : 'text'}
                    value={serviceAccountPassword}
                    onChange={e => setServiceAccountPassword(e.target.value)}
                    disabled={disabled}
                    className='w-full px-3 py-2 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none disabled:opacity-50'
                    placeholder={
                      serviceAccountPasswordOption === 'custom'
                        ? 'Enter custom password'
                        : 'Paste SSH public key'
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
